// cncBreakdown.ts — 저장된 CNC 파트(result 스냅샷)를 견적상세 분해표 한 행으로 shape (순수, 재계산 금지).
//
// 견적 상세 항목 표(이슈 #104)는 CncQuoteResult 의 이미 계산된 필드만 골라 노출한다.
// 여기서 새 원가 수식을 만들지 않는다 — buildup/cncQuote 의 결과를 displayrow 로 평면화만 한다.
//
// 손익분석표(연도별)는 projection.multiYearPnL 로 만든다(여기서 재정의 금지).

import type { CncMaterialResult } from "@/lib/cost/cncQuote";
import { lineCost } from "@/lib/cost/cncQuote";
import { NO_ALLOWANCE_PROCESSES } from "@/lib/cost/labor";
import { type ProjectionResult, multiYearPnL } from "@/lib/cost/projection";
import type { CncPart, CncPartSelection, CncPartVolumes, CncTreatmentLine } from "@/lib/data";

// 연도별 물량 — 기본 2027~2031, "+연도 추가"로 동적 확장. volumes 키는 "yYYYY".
// (기존 견적의 y2025 등 레거시 연도도 키가 있으면 그대로 "입력된 연도"로 인정한다.)
export const DEFAULT_VOLUME_YEARS = [2027, 2028, 2029, 2030, 2031] as const;
const YEAR_KEY_RE = /^y(\d{4})$/;
export const volKey = (y: number): string => `y${y}`;
// selection.volumes 에 존재하는 연도 키를 오름차순 숫자로 (avg 등 비-연도 키는 제외).
export function volumeYears(vol: CncPartVolumes): number[] {
  return Object.keys(vol)
    .map((k) => YEAR_KEY_RE.exec(k))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => Number(m[1]))
    .sort((a, b) => a - b);
}

// 견적유형(파트별) — Select 옵션 + 표시 라벨. 미지정/미상 값은 ""(분해표는 공법으로 fallback).
export const PART_QUOTE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "cnc", label: "CNC" },
  { value: "forging", label: "단조" },
  { value: "forging-out", label: "단조(외주)" },
];
export const partQuoteTypeLabel = (v: string): string =>
  PART_QUOTE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? "";

// 분해표 한 행 — 그룹 컬럼(재료비/가공비/기타비용) + 단가/이윤. 모든 값은 result 에서 픽.
export interface BreakdownRow {
  partNo: string; // 품번 ★ 사용자 입력(selection.partNo) — 미입력 시 품목명/재질 fallback
  itemName: string; // 품목명
  method: string; // 공법 (대표 공정명)
  quoteTypeLabel: string; // 견적유형 라벨(CNC/단조/단조(외주)) — 미지정 시 공법(method) fallback
  avgVolume: number; // 연평균 물량(EA) = 세부원가 연평균 × USG — 헤더밴드 연평균물량의 파트 몫
  spec: string; // 규격 ★ 사용자 입력(selection.spec)
  material: string; // 재질 (재료명)
  productWeightG: number; // 제출중량(g)
  // 재료비 그룹
  materialNet: number; // 재료비(원재료비계)
  surface: number; // 표면처리비
  heat: number; // 열처리비
  materialEtc: number; // 기타비 = Σ 기타 라인 (재료비계에 합산되나 그동안 컬럼 미노출)
  treatments: { name: string; cost: number }[]; // 재료비 세부 항목(동적 컬럼) — 표면처리/열처리/기타 라인의 항목명
  materialTotal: number; // 재료비계
  // 가공비 그룹
  cncMachining: number; // CNC가공(검사·포장 제외 공정 합)
  inspectionPacking: number; // 검사/포장(최종검사·포장 공정 합)
  processingTotal: number; // 가공비계
  perProcess: { name: string; cost: number }[]; // 공정별 가공비(동적 컬럼) — 레거시 스냅샷은 고정 2버킷 합성
  // 기타비용 그룹
  manufacturingCost: number; // 제조원가
  generalAdmin: number; // 일반관리비
  materialAdmin: number; // 재료관리비
  mold: number; // 금형비
  misc: number; // 기타비용(₩/ea) — 레거시 스냅샷(미보존)은 0
  // 단가
  internalCost: number; // 내부단가 = 견적가 − 이윤
  supplyPrice: number; // 견적가
  profit: number; // 이윤
  profitRate: number; // 영업이익률(%) = 이윤 / 견적가 × 100
}

export interface BreakdownTotals {
  avgVolume: number; // Σ파트 연평균 물량(EA) — 헤더밴드 "연 평균 물량(EA)" 과 동일
  materialNet: number;
  surface: number;
  heat: number;
  materialEtc: number;
  materialTotal: number;
  cncMachining: number;
  inspectionPacking: number;
  processingTotal: number;
  manufacturingCost: number;
  generalAdmin: number;
  materialAdmin: number;
  mold: number;
  misc: number;
  internalCost: number;
  supplyPrice: number;
  profit: number;
  profitRate: number; // 가중 = Σ이윤 / Σ견적가 × 100
}

export interface BreakdownReport {
  rows: BreakdownRow[];
  totals: BreakdownTotals;
}

// 파트 라벨(재질명/공법명) — 컴포넌트가 마스터에서 resolve 해 주입. 없으면 selection 값/공란.
export interface PartLabel {
  material: string; // 재질명
  method: string; // 대표 공법(공정)명
}

// 재료비 세부 항목 컬럼(내용추가15) — 표면처리/열처리/기타 라인의 "항목명"을 그대로 컬럼으로 노출.
// 금액은 스냅샷 입력(selection)에 엔진과 동일한 lineCost 를 적용 → Σ라인 = result 의
// surfaceCost/heatCost/etcCost 와 일치한다(새 수식 정의 아님).
// 라인이 없는 레거시 스냅샷은 집계값을 기존 고정 라벨(표면처리비/열처리비/기타비) 컬럼으로 노출.
export const TREATMENT_FALLBACK = ["표면처리비", "열처리비", "기타비"] as const;
function treatmentsOf(sel: CncPartSelection, m: CncMaterialResult): { name: string; cost: number }[] {
  const weightG = Number.parseFloat(sel.productWeight) || 0;
  const out: { name: string; cost: number }[] = [];
  const push = (lines: CncTreatmentLine[] | undefined, fallback: string, total: number) => {
    const priced = (lines ?? []).filter((l) => Number.parseFloat(l.unitPrice) || 0);
    if (priced.length === 0) {
      if (total) out.push({ name: fallback, cost: total });
      return;
    }
    for (const l of priced)
      out.push({
        name: l.label?.trim() || fallback,
        cost: lineCost({ unit: l.unit, unitPrice: Number.parseFloat(l.unitPrice) || 0 }, weightG),
      });
  };
  push(sel.surfaceLines, TREATMENT_FALLBACK[0], m.surfaceCost);
  push(sel.heatLines, TREATMENT_FALLBACK[1], m.heatCost);
  push(sel.etcLines, TREATMENT_FALLBACK[2], m.etcCost ?? 0);
  return out;
}

// 재료비 세부 동적 컬럼 = 전 행 항목명의 합집합(표면처리 → 열처리 → 기타, 행 내 입력 순서).
export function treatmentColumns(rows: BreakdownRow[]): string[] {
  const seen: string[] = [];
  for (const r of rows)
    for (const t of r.treatments) {
      if (!seen.includes(t.name)) seen.push(t.name);
    }
  return seen;
}

// 행의 해당 세부 항목 금액(동명 항목 합산). 없으면 0 — 화면은 "-" 로 표시.
export function treatmentCost(row: BreakdownRow, name: string): number {
  return row.treatments.reduce((acc, t) => (t.name === name ? acc + t.cost : acc), 0);
}

export function treatmentColumnTotal(rows: BreakdownRow[], name: string): number {
  return rows.reduce((acc, r) => acc + treatmentCost(r, name), 0);
}

function toRow(part: CncPart, label: PartLabel): BreakdownRow {
  const r = part.result;
  const sel = part.selection;
  // CNC가공 vs 검사·포장 분리 — 엔진이 보장하는 유일하게 안정적인 공정 구분(여유율 0 공정).
  // (다른 소비처 호환용으로 유지 — 화면·엑셀 표는 아래 perProcess 동적 컬럼을 사용.)
  const perProcessSrc = r.processing.perProcess ?? [];
  let inspectionPacking = 0;
  for (const p of perProcessSrc) {
    if (NO_ALLOWANCE_PROCESSES.has(p.processName)) inspectionPacking += p.processingCost;
  }
  const cncMachining = r.processing.processingTotal - inspectionPacking;
  // 공정별 가공비(동적 컬럼) — 레거시 스냅샷(perProcess 미보존)은 기존 고정 2버킷으로 합성해 표시 호환.
  const perProcess = perProcessSrc.length
    ? perProcessSrc.map((p) => ({ name: p.processName, cost: p.processingCost }))
    : [
        { name: "CNC가공", cost: cncMachining },
        // labor.ts NO_ALLOWANCE_PROCESSES 의 정식 공정명과 동일하게 — 레거시+모던 혼합 표에서 컬럼 병합.
        { name: "최종검사/포장", cost: inspectionPacking },
      ];
  const supplyPrice = r.productCost;
  const profit = r.profit;
  const internalCost = supplyPrice - profit;
  // spec 은 모델 추가 없이 cnc passthrough(selection) 에 보존 — 사용자 입력값.
  const spec = (sel as { spec?: string }).spec ?? "";
  return {
    partNo: sel.partNo || sel.itemName || label.material,
    itemName: sel.itemName,
    method: label.method,
    quoteTypeLabel: partQuoteTypeLabel(sel.partQuoteType) || label.method,
    avgVolume: selectionAvgVolume(sel),
    spec,
    material: label.material,
    productWeightG: Number.parseFloat(sel.productWeight) || 0,
    materialNet: r.material.netMaterialCost,
    surface: r.material.surfaceCost,
    heat: r.material.heatCost,
    materialEtc: r.material.etcCost ?? 0,
    treatments: treatmentsOf(sel, r.material),
    materialTotal: r.material.materialCost,
    cncMachining,
    inspectionPacking,
    processingTotal: r.processing.processingTotal,
    perProcess,
    manufacturingCost: r.manufacturingCost,
    generalAdmin: r.generalAdmin,
    materialAdmin: r.materialAdmin,
    mold: r.moldCost,
    misc: r.miscCost ?? 0,
    internalCost,
    supplyPrice,
    profit,
    profitRate: supplyPrice > 0 ? (profit / supplyPrice) * 100 : 0,
  };
}

// 파트 배열 → 분해표(행 + 합계). labels[i] 가 i 번째 파트의 재질/공법 라벨.
export function cncBreakdown(parts: CncPart[], labels: PartLabel[]): BreakdownReport {
  const rows = parts.map((p, i) => toRow(p, labels[i] ?? { material: "", method: "" }));
  const sum = (pick: (r: BreakdownRow) => number): number =>
    rows.reduce((acc, r) => acc + pick(r), 0);
  const supplyPrice = sum((r) => r.supplyPrice);
  const profit = sum((r) => r.profit);
  const totals: BreakdownTotals = {
    avgVolume: sum((r) => r.avgVolume),
    materialNet: sum((r) => r.materialNet),
    surface: sum((r) => r.surface),
    heat: sum((r) => r.heat),
    materialEtc: sum((r) => r.materialEtc),
    materialTotal: sum((r) => r.materialTotal),
    cncMachining: sum((r) => r.cncMachining),
    inspectionPacking: sum((r) => r.inspectionPacking),
    processingTotal: sum((r) => r.processingTotal),
    manufacturingCost: sum((r) => r.manufacturingCost),
    generalAdmin: sum((r) => r.generalAdmin),
    materialAdmin: sum((r) => r.materialAdmin),
    mold: sum((r) => r.mold),
    misc: sum((r) => r.misc),
    internalCost: sum((r) => r.internalCost),
    supplyPrice,
    profit,
    profitRate: supplyPrice > 0 ? (profit / supplyPrice) * 100 : 0,
  };
  return { rows, totals };
}

// 공정 컬럼 순서는 원가마스터의 공정 노출순서(displayOrder)가 원본이다 — 호출부가
// order(= 노출순서대로 정렬한 공정명 목록)를 넘긴다. order 에 없는 공정(마스터에서
// 삭제된 레거시 공정명)은 후미에 입력 순서로 붙는다.
//
// order 미지정(서버 응답 전 fallback·순수 테스트)일 때만 통합 전 고정 규칙을 쓴다:
//   ① 항상 앞: 냉간단조(F) → 냉간단조(H) → 가공(본동) → 가공(B동)
//   ② 그 외 공정: 입력(첫 등장) 순서
//   ③ 항상 뒤: 전수검사 → 최종검사/포장 (최종검사/포장은 언제나 최후미)
export const PROCESS_ORDER_FIRST = [
  "냉간단조(F)",
  "냉간단조(H)",
  "가공(본동)",
  "가공(B동)",
] as const;
export const PROCESS_ORDER_LAST = ["전수검사", "최종검사/포장"] as const;

// 가공비 동적 컬럼 = 전 행 perProcess 공정명의 합집합을 노출순서로 정렬.
export function processColumns(rows: BreakdownRow[], order?: readonly string[]): string[] {
  const seen: string[] = [];
  for (const r of rows)
    for (const p of r.perProcess) {
      if (!seen.includes(p.name)) seen.push(p.name);
    }
  if (order && order.length > 0) {
    const rank = (n: string) => {
      const i = order.indexOf(n);
      return i < 0 ? order.length : i; // 마스터에 없는 공정 → 후미(동순위는 입력순 유지)
    };
    return seen
      .map((n, i) => ({ n, i })) // 동순위(=둘 다 마스터 밖)는 입력 순서 유지
      .sort((a, b) => rank(a.n) - rank(b.n) || a.i - b.i)
      .map((x) => x.n);
  }
  const firsts = PROCESS_ORDER_FIRST.filter((n) => seen.includes(n));
  const lasts = PROCESS_ORDER_LAST.filter((n) => seen.includes(n));
  const pinned = new Set<string>([...firsts, ...lasts]);
  const middle = seen.filter((n) => !pinned.has(n)); // 그 외 — 입력 순서 유지
  return [...firsts, ...middle, ...lasts];
}

// 행의 해당 공정 가공비(동명 공정 합산). 없으면 0 — 화면은 "-" 로 표시.
export function processCost(row: BreakdownRow, name: string): number {
  return row.perProcess.reduce((acc, p) => (p.name === name ? acc + p.cost : acc), 0);
}

// 공정 컬럼의 전 행 합계(합계행용) — 해당 공정 없는 행은 0 기여.
export function processColumnTotal(rows: BreakdownRow[], name: string): number {
  return rows.reduce((acc, r) => acc + processCost(r, name), 0);
}

// 관리비 = 일반관리비 + 재료관리비 — 표(화면·엑셀)에서 두 컬럼을 하나로 합쳐 노출한다.
// (엔진 필드는 그대로 두고 표기만 합산 — 원가 수식 변경 아님.)
export function adminCost(r: { generalAdmin: number; materialAdmin: number }): number {
  return r.generalAdmin + r.materialAdmin;
}

// 견적 상세 품목 카드(내용추가17) — 한 행을 재료비/가공비/기타 3그룹의 세로 목록으로.
// 세부 항목이 가로 열 대신 줄로 쌓이므로 공정이 늘어도 폭이 늘지 않는다.
// 줄은 분해표와 같은 픽(treatmentCost/processCost/adminCost), 계는 분해표 필드 그대로 —
// 0 인 세부 줄은 생략(재료비·관리비 줄은 표의 필수 컬럼이었으므로 항상 노출).
export interface CostLine {
  name: string;
  cost: number;
}
export interface CostGroup {
  key: "material" | "processing" | "other";
  label: string;
  lines: CostLine[];
  total: number;
}
export function costGroups(row: BreakdownRow, order?: readonly string[]): CostGroup[] {
  const one = [row];
  const material: CostLine[] = [
    { name: "재료비", cost: row.materialNet },
    ...visibleTreatmentColumns(one).map((name) => ({ name, cost: treatmentCost(row, name) })),
  ];
  const processing = visibleProcessColumns(one, order).map((name) => ({
    name,
    cost: processCost(row, name),
  }));
  const other: CostLine[] = [
    { name: "관리비", cost: adminCost(row) },
    ...(row.mold ? [{ name: "금형비", cost: row.mold }] : []),
    ...(row.misc ? [{ name: "기타비용", cost: row.misc }] : []),
  ];
  return [
    { key: "material", label: "재료비", lines: material, total: row.materialTotal },
    { key: "processing", label: "가공비", lines: processing, total: row.processingTotal },
    {
      key: "other",
      label: "기타",
      lines: other,
      total: adminCost(row) + row.mold + row.misc,
    },
  ];
}

// 컬럼 숨김(내용추가12): 해당 행 묶음(=인쇄 1페이지)에서 전부 0/null/undefined 인 컬럼은 감춘다.
export function hasAnyValue(
  rows: BreakdownRow[],
  pick: (r: BreakdownRow) => number | null | undefined
): boolean {
  return rows.some((r) => !!pick(r));
}

// 값이 하나라도 있는 공정 컬럼만 — 순서는 processColumns 규칙 그대로.
export function visibleProcessColumns(rows: BreakdownRow[], order?: readonly string[]): string[] {
  return processColumns(rows, order).filter((name) =>
    hasAnyValue(rows, (r) => processCost(r, name))
  );
}

// 페이지(청크)별 노출 컬럼 — 재료비/재료계·관리비 등 필수 컬럼은 항상 노출.
export interface VisibleColumns {
  treat: string[]; // 재료비 세부 항목 동적 컬럼(표면처리/열처리/기타 항목명)
  proc: string[]; // 공정별 가공비 동적 컬럼
  mold: boolean; // 금형비
  misc: boolean; // 기타비용
}
// 값이 하나라도 있는 재료비 세부 항목만 — 순서는 treatmentColumns 규칙 그대로.
export function visibleTreatmentColumns(rows: BreakdownRow[]): string[] {
  return treatmentColumns(rows).filter((name) =>
    hasAnyValue(rows, (r) => treatmentCost(r, name))
  );
}
export function visibleColumns(rows: BreakdownRow[], order?: readonly string[]): VisibleColumns {
  return {
    treat: visibleTreatmentColumns(rows),
    proc: visibleProcessColumns(rows, order),
    mold: hasAnyValue(rows, (r) => r.mold),
    misc: hasAnyValue(rows, (r) => r.misc),
  };
}

// USG 수량 배수 — 물량 정보(연도값)에 곱한다. 빈값/0/음수는 ×1(곱셈 미적용).
//   예: 2025년 물량 10000, USG 수량 2 → 유효물량 20000.
export function usgFactor(sel: CncPartSelection): number {
  const q = Number.parseFloat(sel.usgQty);
  return Number.isFinite(q) && q > 0 ? q : 1;
}

// 연평균(그리드 표시) = 값이 입력된 연도의 "입력값" 평균 — USG 미곱(명세: 연평균은 USG와 곱하지 않음).
//   예: USG 2, 입력 2·3·4·5·6만 → 연평균 (2+3+4+5+6)만 ÷ 5 = 4만.
export function selectionAvgInput(sel: CncPartSelection): number {
  const vals = volumeYears(sel.volumes)
    .map((y) => Number.parseFloat(sel.volumes[volKey(y)]))
    .filter((n) => Number.isFinite(n) && n > 0);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
}

// 유효 연평균 물량(EA) = 연평균(입력) × USG — 제출 견적서 연간수량·헤더 연평균물량용(실물량).
export function selectionAvgVolume(sel: CncPartSelection): number {
  return selectionAvgInput(sel) * usgFactor(sel);
}
export function partAvgVolume(part: CncPart): number {
  return selectionAvgVolume(part.selection);
}

// 헤더밴드 "연 평균 물량"(EA) = 파트별 유효 연평균 합.
export function avgAnnualVolume(parts: CncPart[]): number {
  return parts.reduce((sum, p) => sum + selectionAvgVolume(p.selection), 0);
}

// 손익분석표 — 아이템(파트)별 매출 framing (#145):
//   물량(파트, 연도) = 파트 연도값 × USG
//   매출(파트, 연도) = 파트 견적가 × 파트 물량      ← 파트별로 곱한 뒤 합한다
//   이익(파트, 연도) = 파트 단위이윤 × 파트 물량      (Σ견적가 × Σ물량 아님)
//   이익률(파트)     = 이익 / 매출
//   합계 = Σ파트 — 화면·엑셀 손익분석표의 마지막 합계 블록.
// 파트 1개 = plan 1개로 multiYearPnL(제네릭 엔진, 수식 재정의 금지)에 넘긴다.
// (labels 는 호출부 시그니처 호환용 — 집계엔 미사용.)
export function cncPnL(parts: CncPart[], _labels: PartLabel[]): ProjectionResult {
  // 연도 = 전 파트 volumes 에 존재하는 연도의 합집합(오름차순). 없으면 기본 2027~2031.
  const yearSet = new Set<number>();
  for (const p of parts) for (const y of volumeYears(p.selection.volumes)) yearSet.add(y);
  const years = yearSet.size ? [...yearSet].sort((a, b) => a - b) : [...DEFAULT_VOLUME_YEARS];

  const plans = parts.map((p, i) => {
    // 단가·이윤은 원단위 반올림 후 곱한다 — 분해표 표기값(정수) × 물량 = 매출이 되어야
    // 고객 검산과 맞는다. 반올림은 파트 레벨 1회.
    const supply = Math.round(p.result.productCost);
    const profit = Math.round(p.result.profit);
    const f = usgFactor(p.selection);
    const sel = p.selection;
    return {
      name: sel.partNo || sel.itemName || `품목 ${i + 1}`,
      sopInternalCost: supply - profit,
      quotePrices: years.map(() => supply),
      annualVolume: years.map((y) => (Number.parseFloat(sel.volumes[volKey(y)] ?? "") || 0) * f),
    };
  });
  return multiYearPnL(plans, { years });
}
